import axios from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Suggestion } from 'types/api/envScales/Suggestion';
import { ObservationValidations } from 'types/api/envScales/ObservationValidations';

export type UpdateObservationParams = {
  selected_strength_dimension_id?: number | null;
  selected_strength_indicator_id?: number | null;
  selected_improve_dimension_id?: number | null;
  selected_improve_indicator_id?: number | null;
  strength_text?: string;
  improve_text?: string;
  scores_changed?: boolean;
};

export function updateObservation(
  id: number,
  params: UpdateObservationParams | Record<string, unknown> = {}
) {
  return axios.put(`/api/v1/observations/${id}`, params);
}

/**
 * Persists observation changes. Callers are expected to debounce their own
 * input; this mutation intentionally does not invalidate the suggestions
 * query, because refetching would re-seed the form and clobber in-flight edits.
 */
export function useUpdateObservation(id: number) {
  return useMutation({
    mutationFn: (params: UpdateObservationParams) =>
      updateObservation(id, params)
  });
}

export function getObservationSuggestions(id: number) {
  return axios.get<Suggestion>(`/api/v1/observations/${id}/suggestions`);
}

/**
 * Suggestions are derived from cycle scores, so they can change while the
 * observer is editing cycles elsewhere and must be re-read on every visit.
 *
 * `gcTime: 0` drops the entry as soon as the last consumer unmounts, so
 * returning to the page always refetches rather than rendering a cached copy.
 * Because nothing is cached across visits, `staleTime` only governs a single
 * visit, where being pinned as fresh means several consumers share one request
 * and no background refetch can overwrite what the observer has typed.
 */
export function useObservationSuggestions(id: number) {
  return useQuery({
    queryKey: ['envScales', 'observations', id, 'suggestions'],
    queryFn: () => getObservationSuggestions(id).then(res => res.data),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
}

export function getObservationValidations(id: number) {
  return axios.get<ObservationValidations>(
    `/api/v1/observations/${id}/validations`
  );
}

export function getObservationAverages(id: number) {
  return axios.get(`/api/v1/observations/${id}/score_averages`);
}

export function uploadAttachment(id: number, payload: FormData, config = {}) {
  return axios.post(`/api/v1/observations/${id}/attachments`, payload, config);
}

export function deleteAttachment(id: number, attachmentId: number) {
  return axios.delete(`/api/v1/observations/${id}/attachments/${attachmentId}`);
}

export function getAttachments(id: number) {
  return axios.get(`/api/v1/observations/${id}/attachments`);
}

export function getKalturaClientToken(observationId: number) {
  return axios.get(`/api/v1/observations/${observationId}/videos/client_token`);
}

export function attachVideo(
  observationId: number,
  payload: { upload_token: string; name: string }
) {
  return axios.post(`/api/v1/observations/${observationId}/videos`, payload);
}

export function deleteVideo(observationId: number, videoId: number) {
  return axios.delete(
    `/api/v1/observations/${observationId}/videos/${videoId}`
  );
}

export function getVideo(observationId: number, videoId: string) {
  return axios.get(`/api/v1/observations/${observationId}/videos/${videoId}`);
}
